import React, { useState } from 'react';
import { Check, Home, Image as ImageIcon, Sparkles, X, Heart, Package, Maximize2, ExternalLink, Camera } from 'lucide-react';
import { RoomRenderDialog } from './RoomRenderDialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { ColorImage } from '../../admin/fabric/ColorImage';
import { ROOM_TYPES, roomLabel } from '../../../lib/roomTypes';
import { fabricIdentifier } from '../../../lib/fabricLabels';
import { StockDot } from '../../fabric/StockDot';
import { DiscontinuedAlternatives } from './DiscontinuedAlternatives';
import { brandLabel } from '../../../lib/brandNames';

const Spec = ({ label, value }) =>
  value ? (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
      <p className="text-sm text-neutral-800">{Array.isArray(value) ? value.join(', ') : value}</p>
    </div>
  ) : null;

export const FabricDetailPanel = ({
  entry, isSelected, onSelect, onClose, selectLabel = 'Select this fabric',
  isFavorite, onToggleFavorite, inSampleRequest, onRequestSample, onEnlarge, roomType, onRoomTypeChange,
  allEntries = [], onPickAlternative,
}) => {
  const exactRoomUrl = roomType ? entry.room_visualizations?.[roomType] : null;
  const roomUrl = exactRoomUrl || entry.room_visualization_url || entry.ai_visualization_url;
  const [view, setView] = useState(entry.image_url ? 'swatch' : 'room');
  const [roomRenderOpen, setRoomRenderOpen] = useState(false);
  const src = view === 'room' ? roomUrl : entry.image_url;
  const link = entry.collection_website_url || entry.brand_website;

  return (
    <div className="flex flex-col h-full bg-white" data-testid="fabric-detail-panel">
      <div className="flex items-start justify-between p-4 border-b">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full border-2 border-white shadow shrink-0" style={{ backgroundColor: entry.hex_color || '#ccc' }} />
          <div className="min-w-0">
            <h3 className="font-serif text-lg leading-tight truncate flex items-center gap-2" data-testid="fabric-detail-name">{entry.name}<StockDot status={entry.stock_status} discontinued={entry.discontinued} testId="fabric-detail-stock" side="bottom" /></h3>
            <p className="text-xs text-neutral-500 truncate" title={fabricIdentifier(entry)} data-testid="fabric-detail-path">{[brandLabel(entry.brand_name), entry.collection_name, entry.material_name].filter(Boolean).join(' › ')}</p>
          </div>
        </div>
        <div className="flex items-center shrink-0 -mr-2">
          {onToggleFavorite && (
            <Button variant="ghost" size="icon" onClick={() => onToggleFavorite(entry)} aria-pressed={isFavorite} data-testid="fabric-detail-heart" className={isFavorite ? 'text-rose-500 hover:text-rose-600' : 'text-neutral-500'}>
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="fabric-detail-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto flex-1">
        <div
          className="relative aspect-[4/3] rounded-xl overflow-hidden bg-neutral-100 group cursor-zoom-in"
          onClick={() => onEnlarge?.(entry)}
          data-testid="fabric-detail-image"
        >
          <ColorImage key={src} src={src} alt={entry.name} hex={entry.hex_color} className="w-full h-full" eager fit={view === 'swatch' ? 'contain' : 'cover'} />
          {onEnlarge && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEnlarge(entry); }}
              className="absolute bottom-2 right-2 h-8 px-2.5 rounded-md bg-white/90 hover:bg-white text-xs font-medium flex items-center gap-1.5 shadow"
              data-testid="fabric-detail-enlarge"
            >
              <Maximize2 className="w-3.5 h-3.5" /> Enlarge
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
          <Button size="sm" variant={view === 'swatch' ? 'default' : 'ghost'} className="flex-1 h-8 gap-1.5" disabled={!entry.image_url} onClick={() => setView('swatch')} data-testid="fabric-detail-view-swatch">
            <ImageIcon className="w-3.5 h-3.5" /> Swatch
          </Button>
          <Button size="sm" variant={view === 'room' ? 'default' : 'ghost'} className="flex-1 h-8 gap-1.5" disabled={!roomUrl} onClick={() => setView('room')} data-testid="fabric-detail-view-room">
            <Home className="w-3.5 h-3.5" /> In a room
          </Button>
        </div>
        {view === 'room' && onRoomTypeChange && (
          <div className="flex flex-wrap gap-1.5" data-testid="fabric-detail-room-picker">
            {ROOM_TYPES.map((room) => {
              const available = !!entry.room_visualizations?.[room.id];
              return (
                <button
                  key={room.id}
                  type="button"
                  onClick={() => onRoomTypeChange(room.id)}
                  data-testid={`fabric-detail-room-${room.id}`}
                  title={available ? `See in ${room.label}` : `${room.label} scene not generated yet`}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                    roomType === room.id ? 'bg-neutral-900 text-white border-neutral-900' : available ? 'border-neutral-300 hover:border-neutral-500' : 'border-dashed border-neutral-200 text-neutral-400'
                  }`}
                >
                  {room.label}
                </button>
              );
            })}
          </div>
        )}
        {view === 'room' && roomUrl && (
          <p className="text-xs text-center text-neutral-500 flex items-center justify-center gap-1" data-testid="fabric-detail-room-caption">
            <Sparkles className="w-3 h-3" />
            {exactRoomUrl || !roomType
              ? `AI-generated ${roomLabel(roomType || entry.visualization_room_type).toLowerCase()} visualization`
              : `${roomLabel(roomType)} scene not generated yet — showing ${roomLabel(entry.visualization_room_type).toLowerCase()}`}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {entry.fabric_type && <Badge variant="secondary">{entry.fabric_type}</Badge>}
          {entry.code && <Badge variant="outline" className="font-mono">{entry.code}</Badge>}
          {entry.hex_color && <Badge variant="outline" className="font-mono">{entry.hex_color}</Badge>}
        </div>

        <DiscontinuedAlternatives entry={entry} allEntries={allEntries} onPick={onPickAlternative} />

        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
          <Spec label="Openness" value={entry.openness} />
          <Spec label="Composition" value={entry.composition} />
          <Spec label="Widths" value={entry.widths} />
        </div>

        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-amber-700 hover:underline" data-testid="fabric-detail-brand-link">
            <ExternalLink className="w-3.5 h-3.5" /> View {entry.collection_website_url ? `${entry.collection_name} on ${brandLabel(entry.brand_name)}` : `${brandLabel(entry.brand_name)} website`}
          </a>
        )}
      </div>

      <div className="p-4 border-t space-y-2">
        <Button variant="outline" onClick={() => setRoomRenderOpen(true)} className="w-full gap-2 border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="fabric-detail-room-render-btn">
          <Camera className="w-4 h-4" /> See it in your room
        </Button>
        <RoomRenderDialog open={roomRenderOpen} onOpenChange={setRoomRenderOpen} entry={entry} />
        {onRequestSample && (
          <Button
            variant="outline"
            onClick={() => onRequestSample(entry)}
            className={`w-full gap-2 ${inSampleRequest ? 'border-green-500 text-green-700' : ''}`}
            data-testid="fabric-detail-sample-btn"
          >
            {inSampleRequest ? <Check className="w-4 h-4" /> : <Package className="w-4 h-4" />}
            {inSampleRequest ? 'Added to sample request' : 'Request a sample'}
          </Button>
        )}
        {onSelect && (
          <Button onClick={() => onSelect(entry)} className="w-full gap-2 bg-neutral-900 hover:bg-neutral-800" data-testid="fabric-detail-select-btn">
            <Check className="w-4 h-4" /> {isSelected ? 'Selected' : selectLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FabricDetailPanel;
